import { Fragment, useEffect, useRef } from 'react';

import {
  useChatAutoScroll,
  useGetScheduledChatDetails,
  useInterruptChat,
  useSocket,
  useTextChat,
} from '@mint-lib/ai-chat';
import {
  AiMessage,
  ReasoningStepsLoader,
  UserMessage,
} from '@mint-lib/ai-chat/components';
import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';
import {
  differenceInDays,
  differenceInHours,
  isToday,
  isYesterday,
} from 'date-fns';

import AgenticAiChatSkeleton from '../../molecules/AgenticAiChat/AgenticAiChatSkeleton.jsx';
import { MessageDateSeparator } from './MessageDateSeparator.jsx';

const getScheduleStartText = (t: TFunction, startDate: string) => {
  const startDateUtc = new Date(startDate + 'Z');
  const now = new Date();
  const hoursDiff = differenceInHours(startDateUtc, now);

  if (hoursDiff < 0) {
    return t('This chat will start soon');
  }
  if (hoursDiff < 24) {
    return t('This chat will start in {{count}} hours', { count: hoursDiff });
  }

  return t('This chat will start in {{count}} days', {
    count: differenceInDays(startDateUtc, now),
  });
};

type Props = {
  /** Reports the settle gate state so the parent can hide the chat input
   * together with the content hidden behind the skeleton. */
  onSettlingChange?: (isSettling: boolean) => void;
};

const Messages = ({ onSettlingChange }: Props) => {
  const { formatDate } = useDateFormatter();
  const { chatId } = useSocket();
  const {
    chatMessages,
    isAiProcessing: isAiProcessingText,
    reasoningMessages,
    isRefetching: isChatHistoryRefetching,
    isChatHistoryLoading,
  } = useTextChat();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scheduleDetails = useGetScheduledChatDetails(chatId);

  const { isInterrupted } = useInterruptChat(chatId);

  const { t } = useTranslation('@mint-lib/agentic-ai-circuit');
  const reasoningLoaderData = reasoningMessages.find((rm) => !rm.isDone)?.data;

  const getDateLabel = (createdAt: string) => {
    const date = new Date(createdAt + 'Z');
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';

    return formatDate(date, 'dd/MM/yyyy hh:mm a');
  };

  const lastMessage = chatMessages.at(-1);

  const isWelcomeMsgVisible = Boolean(
    (chatId && !chatMessages.length) ||
    (isChatHistoryRefetching &&
      lastMessage?.message.type === 'ai' &&
      !!lastMessage?.message.isSystemError),
  );

  const { isSettling, bottomRef, lastUserMessageRef, setContentRef } =
    useChatAutoScroll({
      chatId: chatId ?? '',
      containerRef,
      isChatHistoryLoading,
      isWelcomeMsgVisible,
      lastMessage,
    });

  useEffect(() => {
    onSettlingChange?.(isSettling);
    // The cleanup also covers unmount (chat closed), so the input is never
    // left hidden by a stale "settling" value.
    return () => onSettlingChange?.(false);
  }, [isSettling, onSettlingChange]);

  if (!isChatHistoryLoading && isWelcomeMsgVisible && chatId) {
    return (
      <div
        className={
          'flex h-full w-full max-w-full flex-col items-start gap-6 self-center overflow-y-auto'
        }
      >
        <AiMessage
          text={t('Hi!\n\nAsk me anything you need.')}
          chatId={chatId}
          date=""
        />
      </div>
    );
  }

  if (!isChatHistoryLoading && scheduleDetails?.status === 'scheduled') {
    return (
      <div className="flex h-full w-full flex-col">
        <MessageDateSeparator
          date={scheduleDetails.startDate}
          scheduleDetails={scheduleDetails}
        />
        <div className="flex flex-1 items-center justify-center">
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              color: 'tertiary',
            })}
          >
            {getScheduleStartText(t, scheduleDetails.startDate)}
          </p>
        </div>
      </div>
    );
  }

  if (chatId || isChatHistoryLoading) {
    return (
      <div
        className={
          // useChatAutoScroll pins the view manually, native anchoring is off
          'flex h-full w-full flex-col self-center overflow-y-auto [overflow-anchor:none]'
        }
        ref={containerRef}
      >
        {/* One skeleton element across both phases — remounting it would
            restart the pulse animation */}
        {(isChatHistoryLoading || isSettling) && (
          <div className="flex w-full flex-col items-center justify-center">
            <AgenticAiChatSkeleton />
          </div>
        )}

        <div
          ref={isChatHistoryLoading ? undefined : setContentRef}
          className={cn(
            'flex w-full flex-col gap-6',
            // While settling the content is laid out but not shown, so AG
            // Grid and charts can measure themselves behind the skeleton
            isSettling && 'invisible',
            isChatHistoryLoading && 'hidden',
          )}
        >
          {chatMessages.map(({ message, messageId, createdAt }, i, arr) => {
            const currentLabel = getDateLabel(createdAt);
            const isVisibleDateChip =
              i === 0 || currentLabel !== getDateLabel(arr[i - 1].createdAt);

            const { additionalMetadata, type } = message;

            return (
              <Fragment key={messageId}>
                {isVisibleDateChip && (
                  <MessageDateSeparator
                    date={currentLabel}
                    scheduleDetails={scheduleDetails}
                  />
                )}

                {type === 'ai' ? (
                  <AiMessage
                    isInterrupted={isInterrupted}
                    date={currentLabel}
                    isLast={lastMessage?.messageId === messageId}
                    chatId={chatId}
                    additionalMetadata={
                      additionalMetadata as Chat.AiChatMessageRecordType['message']['additionalMetadata']
                    }
                    isSystemError={message.isSystemError}
                    text={message.content}
                    messageId={messageId}
                    reasoningData={
                      reasoningMessages.find((rm) => rm.messageId === messageId)
                        ?.data
                    }
                    webSources={additionalMetadata.metadata?.webSearchSources}
                    knowledgeBaseSources={
                      additionalMetadata.metadata?.knowledgeBaseSources
                    }
                    isFullscreen
                    avatarSize={'small'}
                    containerRef={containerRef}
                  />
                ) : (
                  <div
                    ref={
                      lastMessage?.messageId === messageId
                        ? lastUserMessageRef
                        : undefined
                    }
                  >
                    <UserMessage
                      text={message.content}
                      isCancelled={message?.additionalMetadata?.cancelled}
                    />
                  </div>
                )}
              </Fragment>
            );
          })}

          {isAiProcessingText && !isInterrupted && (
            <ReasoningStepsLoader reasoningData={reasoningLoaderData} />
          )}

          <div ref={bottomRef} className="min-h-px" />
        </div>
      </div>
    );
  }

  return null;
};

export default Messages;
